import type { Lang } from "@/i18n/translations";

/** URL de la politique de confidentialité dans chaque langue. */
export const PRIVACY_ROUTES: Partial<Record<Lang, string>> = {
  fr: "/confidentialite",
  en: "/en/privacy-policy",
  es: "/es/politica-de-privacidad",
};

export function privacyPathFor(lang: Lang): string {
  return PRIVACY_ROUTES[lang] ?? "/confidentialite";
}

export type PolicySection = {
  title: string;
  paragraphs?: string[];
  bullets?: string[];
};

export type PolicyCopy = {
  eyebrow: string;
  title: string;
  titleAccent: string;
  updated: string;
  intro: string;
  sections: PolicySection[];
  contactEmail: string;
  cnilLabel: string;
  cnilHref: string;
};

const EMAIL = "reservations@tspvip.com";

/* ------------------------------------------------------------------ */
/* Français                                                            */
/* ------------------------------------------------------------------ */

const FR: PolicyCopy = {
  eyebrow: "Mentions",
  title: "Politique de",
  titleAccent: "confidentialité",
  updated: "Dernière mise à jour : 6 septembre 2026",
  intro:
    "La présente politique décrit la manière dont TSPVIP — service de chauffeur privé, transferts aéroport, mise à disposition et transport événementiel — collecte, utilise, conserve et protège vos données personnelles, conformément au Règlement général sur la protection des données (RGPD) et à la loi Informatique et Libertés.",
  sections: [
    {
      title: "Responsable du traitement",
      paragraphs: [
        "Le responsable du traitement est la SARL TSPVIP, dont le siège social est situé 31 Rue des Peupliers, 92000 Nanterre, France.",
        "Pour toute question relative à vos données, écrivez à reservations@tspvip.com ou par courrier à l'adresse ci-dessus.",
      ],
    },
    {
      title: "Données collectées",
      paragraphs: ["Selon votre utilisation du site, nous sommes susceptibles de collecter :"],
      bullets: [
        "Identité et coordonnées : prénom, nom, société, adresse e-mail, numéro de téléphone.",
        "Détails du déplacement : type de prestation, véhicule souhaité, lieu de départ et d'arrivée, date et heure, nombre de passagers, numéro de vol ou de train, précisions libres.",
        "Estimation : montant calculé, date et heure de l'affichage de l'estimation, référence de la demande.",
        "Échanges avec le Concierge TSPVIP (assistant conversationnel) : contenu des messages et informations de trajet que vous y indiquez.",
        "Données techniques : pages consultées, page d'entrée, identifiant de session pseudonyme, horodatage, données de connexion générées par l'hébergeur.",
        "Préférences de prospection : accord ou refus de recevoir des offres par e-mail ou par SMS, avec la date et la version du texte accepté.",
      ],
    },
    {
      title: "Champs obligatoires et facultatifs",
      paragraphs: [
        "Les champs signalés comme obligatoires (prénom, nom, e-mail, téléphone et les éléments du trajet) sont nécessaires pour calculer votre estimation et vous répondre. Sans eux, la demande ne peut pas être traitée.",
        "Les autres champs (société, précisions, numéro de vol, cases relatives aux offres commerciales) sont facultatifs et sans conséquence sur l'obtention de votre estimation ou de votre devis.",
      ],
    },
    {
      title: "Finalités et bases légales",
      bullets: [
        "Calculer votre estimation, établir votre devis et en assurer le suivi : mesures précontractuelles prises à votre demande puis exécution du contrat (article 6.1.b du RGPD).",
        "Répondre à vos messages et à vos échanges avec le Concierge TSPVIP : mesures précontractuelles ou intérêt légitime à répondre (articles 6.1.b et 6.1.f).",
        "Améliorer nos services et mesurer l'activité du site au moyen d'indicateurs internes : intérêt légitime (article 6.1.f).",
        "Envoyer des offres ou actualités commerciales : votre consentement préalable, libre, spécifique et révocable (article 6.1.a).",
        "Respecter nos obligations légales, comptables et fiscales : article 6.1.c.",
      ],
    },
    {
      title: "Concierge TSPVIP et préremplissage",
      paragraphs: [
        "Le site propose un assistant conversationnel. Les messages sont transmis à un fournisseur de modèle de langage agissant pour le compte de TSPVIP.",
        "Les informations que vous y communiquez servent uniquement à répondre à votre demande, à préremplir le formulaire de devis, à calculer l'estimation et à assurer le suivi du dossier. Elles ne sont pas utilisées automatiquement pour vous envoyer de la publicité.",
        "Les champs préremplis restent modifiables et vous pouvez les corriger ou les vider avant tout envoi. L'assistant ne prend aucune décision automatisée produisant des effets juridiques : chaque devis est validé par une personne de l'équipe TSPVIP.",
        "Conformément au règlement (UE) 2024/1689 (AI Act), TSPVIP vous informe que vous interagissez avec un système d'intelligence artificielle et vous donne la possibilité de contacter à tout moment un interlocuteur humain.",
      ],
    },
    {
      title: "Enregistrement des étapes de votre demande",
      paragraphs: [
        "Les visites de la page de devis et le fait qu'un formulaire ait été commencé sont enregistrés sous une forme pseudonyme, sans vos coordonnées, afin de mesurer l'usage du service.",
        "Lorsque vous cliquez sur « Afficher mon estimation », votre fiche complète est enregistrée ou mise à jour avec le statut « Estimation affichée — devis non confirmé », le montant présenté et l'horodatage. Si vous ne demandez pas de devis ferme, cette fiche reste dans notre outil de suivi jusqu'à l'expiration du délai de conservation indiqué plus bas.",
      ],
    },
    {
      title: "Destinataires",
      bullets: [
        "En interne : les personnes de TSPVIP chargées des réservations, du service client et de la facturation, dans la limite de ce qui est nécessaire à leur mission. L'accès à l'outil de suivi des devis est réservé à des comptes administrateurs authentifiés.",
        "Le cas échéant, nos chauffeurs partenaires, pour les seules informations nécessaires à la réalisation de la course.",
        "Les autorités administratives ou judiciaires lorsque la loi l'exige.",
      ],
      paragraphs: ["Vos données ne sont ni vendues ni louées."],
    },
    {
      title: "Prestataires techniques",
      paragraphs: ["Nous faisons appel aux prestataires suivants, agissant pour notre compte :"],
      bullets: [
        "Hébergement du site, de la base de données et de l'authentification : Lovable Cloud (infrastructure Supabase) et Cloudflare pour la diffusion du site.",
        "Envoi des e-mails transactionnels (confirmations, devis) : Resend.",
        "Assistant conversationnel et rédaction assistée : passerelle d'intelligence artificielle Lovable AI.",
        "Calcul des distances routières : Google Maps Platform (API Routes).",
        "Gestion du bandeau et des préférences de cookies : Secure Privacy.",
      ],
    },
    {
      title: "Transferts hors Union européenne",
      paragraphs: [
        "Certains de ces prestataires sont établis aux États-Unis ou peuvent y opérer des traitements. Ces transferts sont encadrés par les clauses contractuelles types de la Commission européenne et, le cas échéant, par le cadre de protection des données UE–États-Unis, complétés de mesures techniques telles que le chiffrement en transit.",
        "Vous pouvez demander une copie des garanties applicables en écrivant à reservations@tspvip.com.",
      ],
    },
    {
      title: "Durées de conservation",
      bullets: [
        "Estimation ou devis sans suite : 12 mois à compter du dernier contact émanant de vous. À l'échéance, la fiche est supprimée ou rendue non identifiante par une tâche automatique.",
        "Données utilisées pour la prospection commerciale (offres et actualités) : 3 ans à compter de leur collecte ou du dernier contact émanant de vous.",
        "Réservation confirmée, facture, paiement, litige ou obligation comptable : la durée légale propre à chaque document s'applique séparément, notamment dix ans pour les pièces comptables.",
        "Échanges avec le Concierge TSPVIP : 90 jours maximum, sauf s'ils donnent lieu à une demande de devis ou à une réservation.",
        "Journaux techniques et données de connexion : 12 mois maximum.",
        "Preuve d'un refus de prospection : conservée sous forme minimale, dans une liste d'opposition distincte utilisée uniquement pour ne plus vous solliciter.",
      ],
      paragraphs: [
        "Le simple écoulement du temps, l'ouverture d'un e-mail ou une action interne de TSPVIP ne constituent pas un nouveau contact de votre part et ne prolongent pas ces durées. Une estimation non confirmée n'est pas une pièce comptable et n'est pas conservée à ce titre.",
      ],
    },
    {
      title: "Cookies et traceurs",
      paragraphs: [
        "Les cookies strictement nécessaires au fonctionnement du site sont déposés sans consentement. Les traceurs de mesure d'audience ou de publicité, lorsqu'ils sont utilisés, ne sont déposés qu'après votre accord recueilli via le bandeau de gestion des cookies.",
        "Vous pouvez accepter, refuser ou modifier votre choix à tout moment depuis ce bandeau, ainsi que par les réglages de votre navigateur. La durée de vie des traceurs de mesure d'audience n'excède pas 13 mois et les informations collectées ne sont pas conservées au-delà de 25 mois.",
        "Le refus des traceurs non essentiels n'empêche ni l'utilisation du formulaire de devis, ni le Concierge TSPVIP, ni l'affichage de votre estimation.",
      ],
    },
    {
      title: "Offres et actualités par e-mail et SMS",
      paragraphs: [
        "Les cases permettant de recevoir nos offres par e-mail ou par SMS sont facultatives et décochées par défaut. Elles ne conditionnent ni l'affichage de l'estimation, ni l'envoi du devis.",
        "Nous enregistrons la date, l'heure, le canal et la version du texte accepté afin de pouvoir prouver votre consentement. Vous pouvez le retirer à tout moment, par le lien de désinscription des messages commerciaux ou en écrivant à reservations@tspvip.com : les sollicitations concernées cessent immédiatement et seules les informations minimales nécessaires à votre opposition sont conservées.",
        "Chaque message commercial contient un lien de désinscription qui ouvre une page sécurisée de gestion des préférences (www.tspvip.com/preferences). Cette page permet de désactiver séparément les e-mails, les SMS ou les deux, sans créer de compte, sans recommuniquer vos coordonnées, sans justifier votre décision et sans nous téléphoner. La prise en compte est immédiate et horodatée.",
        "Le retrait de votre consentement publicitaire n'entraîne pas la suppression des documents devant être conservés au titre d'une réservation, d'une facture ou d'un litige.",
      ],
    },
    {
      title: "Vos droits",
      paragraphs: [
        "Vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation du traitement, d'opposition et, lorsque le traitement repose sur le contrat ou le consentement, de portabilité. Vous pouvez également retirer à tout moment un consentement donné, sans que cela remette en cause la licéité du traitement effectué auparavant.",
        "Vous pouvez enfin définir des directives relatives au sort de vos données après votre décès.",
      ],
    },
    {
      title: "Comment exercer vos droits",
      paragraphs: [
        "Adressez votre demande à reservations@tspvip.com ou par courrier à SARL TSPVIP, 31 Rue des Peupliers, 92000 Nanterre. Précisez l'objet de votre demande ; un justificatif d'identité peut vous être demandé en cas de doute raisonnable.",
        "Nous répondons dans un délai d'un mois, prolongeable de deux mois si la demande est complexe, auquel cas vous en êtes informé.",
      ],
    },
    {
      title: "Réclamation auprès de la CNIL",
      paragraphs: [
        "Si vous estimez, après nous avoir contactés, que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de la Commission nationale de l'informatique et des libertés (CNIL), 3 place de Fontenoy, TSA 80715, 75334 Paris Cedex 07.",
      ],
    },
    {
      title: "Sécurité",
      paragraphs: [
        "Nous mettons en œuvre des mesures techniques et organisationnelles appropriées : chiffrement des échanges (HTTPS), cloisonnement des accès à la base de données par des règles de sécurité au niveau des lignes, comptes administrateurs nominatifs et authentifiés, limitation des informations enregistrées dans les journaux techniques.",
      ],
    },
  ],
  contactEmail: EMAIL,
  cnilLabel: "CNIL",
  cnilHref: "https://www.cnil.fr/fr/plaintes",
};

/* ------------------------------------------------------------------ */
/* English                                                             */
/* ------------------------------------------------------------------ */

const EN: PolicyCopy = {
  eyebrow: "Legal",
  title: "Privacy",
  titleAccent: "policy",
  updated: "Last updated: 6 September 2026",
  intro:
    "This policy explains how TSPVIP — private chauffeur service, airport transfers, hourly hire and event transport — collects, uses, stores and protects your personal data, in accordance with the General Data Protection Regulation (GDPR) and French data protection law.",
  sections: [
    {
      title: "Data controller",
      paragraphs: [
        "The data controller is SARL TSPVIP, registered office at 31 Rue des Peupliers, 92000 Nanterre, France.",
        "For any question about your data, write to reservations@tspvip.com or by post to the address above.",
      ],
    },
    {
      title: "Data we collect",
      paragraphs: ["Depending on how you use the site, we may collect:"],
      bullets: [
        "Identity and contact details: first name, last name, company, email address, phone number.",
        "Journey details: type of service, requested vehicle, pick-up and drop-off locations, date and time, number of passengers, flight or train number, free-text notes.",
        "Estimate: calculated amount, date and time the estimate was displayed, request reference.",
        "Exchanges with the TSPVIP Concierge (conversational assistant): message content and the journey information you provide there.",
        "Technical data: pages viewed, entry page, pseudonymous session identifier, timestamps, connection data generated by our host.",
        "Marketing preferences: your agreement or refusal to receive offers by email or SMS, with the date and the version of the wording accepted.",
      ],
    },
    {
      title: "Required and optional fields",
      paragraphs: [
        "Fields marked as required (first name, last name, email, phone and the journey details) are necessary to calculate your estimate and reply to you. Without them the request cannot be processed.",
        "The other fields (company, notes, flight number, marketing checkboxes) are optional and have no effect on obtaining your estimate or your quote.",
      ],
    },
    {
      title: "Purposes and legal bases",
      bullets: [
        "Calculating your estimate, issuing your quote and following it up: pre-contractual steps taken at your request, then performance of the contract (Article 6(1)(b) GDPR).",
        "Answering your messages and your exchanges with the TSPVIP Concierge: pre-contractual steps or our legitimate interest in replying (Articles 6(1)(b) and 6(1)(f)).",
        "Improving our services and measuring site activity through internal indicators: legitimate interest (Article 6(1)(f)).",
        "Sending commercial offers or news: your prior, free, specific and revocable consent (Article 6(1)(a)).",
        "Complying with our legal, accounting and tax obligations: Article 6(1)(c).",
      ],
    },
    {
      title: "TSPVIP Concierge and pre-filling",
      paragraphs: [
        "The site offers a conversational assistant. Messages are sent to a language-model provider acting on behalf of TSPVIP.",
        "The information you provide there is used only to answer your request, pre-fill the quote form, calculate the estimate and follow up your file. It is not automatically used to send you advertising.",
        "Pre-filled fields remain editable and you can correct or clear them before sending anything. The assistant makes no automated decision producing legal effects: every quote is validated by a member of the TSPVIP team.",
        "In accordance with Regulation (EU) 2024/1689 (AI Act), TSPVIP informs you that you are interacting with an artificial intelligence system and gives you the option of contacting a human at any time.",
      ],
    },
    {
      title: "Recording the steps of your request",
      paragraphs: [
        "Visits to the quote page and the fact that a form has been started are recorded in pseudonymous form, without your contact details, in order to measure use of the service.",
        "When you click “View my estimate”, your complete record is created or updated with the status “Estimate viewed — quote not confirmed”, the amount displayed and the timestamp. If you do not request a final quote, that record stays in our tracking tool until the retention period stated below expires.",
      ],
    },
    {
      title: "Recipients",
      bullets: [
        "Internally: TSPVIP staff in charge of bookings, customer service and invoicing, limited to what their role requires. Access to the quote tracking tool is restricted to authenticated administrator accounts.",
        "Where relevant, our partner chauffeurs, for the information strictly needed to perform the journey.",
        "Administrative or judicial authorities where the law requires it.",
      ],
      paragraphs: ["Your data is never sold or rented."],
    },
    {
      title: "Technical service providers",
      paragraphs: ["We use the following providers, acting on our behalf:"],
      bullets: [
        "Website, database and authentication hosting: Lovable Cloud (Supabase infrastructure) and Cloudflare for site delivery.",
        "Transactional email delivery (confirmations, quotes): Resend.",
        "Conversational assistant and assisted writing: the Lovable AI gateway.",
        "Road distance calculation: Google Maps Platform (Routes API).",
        "Cookie banner and preference management: Secure Privacy.",
      ],
    },
    {
      title: "Transfers outside the European Union",
      paragraphs: [
        "Some of these providers are established in the United States or may process data there. Such transfers are governed by the European Commission's standard contractual clauses and, where applicable, the EU–US Data Privacy Framework, together with technical measures such as encryption in transit.",
        "You may request a copy of the applicable safeguards by writing to reservations@tspvip.com.",
      ],
    },
    {
      title: "Retention periods",
      bullets: [
        "Estimate or quote with no follow-up: 12 months from your last contact. At that point the record is deleted or made non-identifying by an automated task.",
        "Data used for marketing (offers and news): 3 years from collection or from your last contact.",
        "Confirmed booking, invoice, payment, dispute or accounting obligation: the statutory period specific to each document applies separately, in particular ten years for accounting records.",
        "Exchanges with the TSPVIP Concierge: 90 days maximum, unless they lead to a quote request or a booking.",
        "Technical logs and connection data: 12 months maximum.",
        "Proof of a marketing refusal: kept in minimal form, in a separate suppression list used only to stop contacting you.",
      ],
      paragraphs: [
        "The mere passing of time, the opening of an email or an internal TSPVIP action does not count as a new contact from you and does not extend these periods. An unconfirmed estimate is not an accounting record and is not kept on that basis.",
      ],
    },
    {
      title: "Cookies and trackers",
      paragraphs: [
        "Cookies strictly necessary for the site to work are set without consent. Audience-measurement or advertising trackers, where used, are only set after you agree through the cookie banner.",
        "You can accept, refuse or change your choice at any time from that banner, and through your browser settings. Audience-measurement trackers do not last more than 13 months and the information collected is not kept beyond 25 months.",
        "Refusing non-essential trackers does not prevent you from using the quote form, the TSPVIP Concierge, or seeing your estimate.",
      ],
    },
    {
      title: "Offers and news by email and SMS",
      paragraphs: [
        "The boxes for receiving our offers by email or SMS are optional and unticked by default. They are not a condition for viewing the estimate or receiving the quote.",
        "We record the date, time, channel and version of the wording accepted so we can evidence your consent. You may withdraw it at any time, through the unsubscribe link in commercial messages or by writing to reservations@tspvip.com: the relevant messages stop immediately and only the minimum information needed to honour your objection is kept.",
        "Every commercial message contains an unsubscribe link opening a secure preference page (www.tspvip.com/en/preferences). That page lets you switch off email, SMS or both separately, without creating an account, without giving your details again, without justifying your decision and without calling us. It takes effect immediately and is timestamped.",
        "Withdrawing marketing consent does not delete documents that must be kept for a booking, an invoice or a dispute.",
      ],
    },
    {
      title: "Your rights",
      paragraphs: [
        "You have the right of access, rectification, erasure, restriction of processing, objection and — where processing is based on a contract or consent — data portability. You may also withdraw consent at any time, without affecting the lawfulness of processing carried out beforehand.",
        "You may also give instructions about what happens to your data after your death.",
      ],
    },
    {
      title: "How to exercise your rights",
      paragraphs: [
        "Send your request to reservations@tspvip.com or by post to SARL TSPVIP, 31 Rue des Peupliers, 92000 Nanterre, France. State the purpose of your request; proof of identity may be requested in case of reasonable doubt.",
        "We reply within one month, extendable by two further months for complex requests, in which case you will be informed.",
      ],
    },
    {
      title: "Complaint to the supervisory authority",
      paragraphs: [
        "If, after contacting us, you believe your rights are not respected, you may lodge a complaint with the French data protection authority (CNIL), 3 place de Fontenoy, TSA 80715, 75334 Paris Cedex 07, France.",
      ],
    },
    {
      title: "Security",
      paragraphs: [
        "We implement appropriate technical and organisational measures: encrypted connections (HTTPS), database access partitioned by row-level security rules, named and authenticated administrator accounts, and minimisation of the information stored in technical logs.",
      ],
    },
  ],
  contactEmail: EMAIL,
  cnilLabel: "CNIL",
  cnilHref: "https://www.cnil.fr/en/plaintes",
};

/* ------------------------------------------------------------------ */
/* Español                                                             */
/* ------------------------------------------------------------------ */

const ES: PolicyCopy = {
  eyebrow: "Aviso legal",
  title: "Política de",
  titleAccent: "privacidad",
  updated: "Última actualización: 6 de septiembre de 2026",
  intro:
    "Esta política describe cómo TSPVIP —servicio de chófer privado, traslados al aeropuerto, servicio por horas y transporte de eventos— recoge, utiliza, conserva y protege sus datos personales, conforme al Reglamento General de Protección de Datos (RGPD) y a la normativa francesa de protección de datos.",
  sections: [
    {
      title: "Responsable del tratamiento",
      paragraphs: [
        "El responsable del tratamiento es SARL TSPVIP, con domicilio social en 31 Rue des Peupliers, 92000 Nanterre (Francia).",
        "Para cualquier consulta sobre sus datos, escriba a reservations@tspvip.com o por correo postal a la dirección indicada.",
      ],
    },
    {
      title: "Datos recogidos",
      paragraphs: ["Según el uso que haga del sitio, podemos recoger:"],
      bullets: [
        "Identidad y datos de contacto: nombre, apellidos, empresa, correo electrónico, teléfono.",
        "Datos del trayecto: tipo de servicio, vehículo solicitado, lugar de recogida y de destino, fecha y hora, número de pasajeros, número de vuelo o de tren, comentarios libres.",
        "Estimación: importe calculado, fecha y hora en que se mostró la estimación, referencia de la solicitud.",
        "Conversaciones con el Conserje TSPVIP (asistente conversacional): contenido de los mensajes y datos del trayecto que facilite en ellos.",
        "Datos técnicos: páginas consultadas, página de entrada, identificador de sesión seudonimizado, marcas de tiempo y datos de conexión generados por el alojamiento.",
        "Preferencias comerciales: aceptación o rechazo de recibir ofertas por correo electrónico o SMS, con la fecha y la versión del texto aceptado.",
      ],
    },
    {
      title: "Campos obligatorios y facultativos",
      paragraphs: [
        "Los campos señalados como obligatorios (nombre, apellidos, correo electrónico, teléfono y los datos del trayecto) son necesarios para calcular su estimación y responderle. Sin ellos no es posible tramitar la solicitud.",
        "Los demás campos (empresa, comentarios, número de vuelo, casillas comerciales) son facultativos y no afectan a la obtención de su estimación ni de su presupuesto.",
      ],
    },
    {
      title: "Finalidades y bases jurídicas",
      bullets: [
        "Calcular su estimación, elaborar su presupuesto y realizar su seguimiento: medidas precontractuales adoptadas a petición suya y posterior ejecución del contrato (artículo 6.1.b del RGPD).",
        "Responder a sus mensajes y a sus conversaciones con el Conserje TSPVIP: medidas precontractuales o interés legítimo en responder (artículos 6.1.b y 6.1.f).",
        "Mejorar nuestros servicios y medir la actividad del sitio mediante indicadores internos: interés legítimo (artículo 6.1.f).",
        "Enviar ofertas o novedades comerciales: su consentimiento previo, libre, específico y revocable (artículo 6.1.a).",
        "Cumplir nuestras obligaciones legales, contables y fiscales: artículo 6.1.c.",
      ],
    },
    {
      title: "Conserje TSPVIP y relleno previo",
      paragraphs: [
        "El sitio ofrece un asistente conversacional. Los mensajes se transmiten a un proveedor de modelos de lenguaje que actúa por cuenta de TSPVIP.",
        "La información que facilite se utiliza únicamente para responder a su solicitud, rellenar previamente el formulario de presupuesto, calcular la estimación y realizar el seguimiento del expediente. No se utiliza automáticamente para enviarle publicidad.",
        "Los campos rellenados previamente siguen siendo editables y puede corregirlos o vaciarlos antes de enviar nada. El asistente no adopta ninguna decisión automatizada con efectos jurídicos: cada presupuesto lo valida una persona del equipo TSPVIP.",
        "Conforme al Reglamento (UE) 2024/1689 (AI Act), TSPVIP le informa de que está interactuando con un sistema de inteligencia artificial y le ofrece la posibilidad de contactar en todo momento con una persona.",
      ],
    },
    {
      title: "Registro de las etapas de su solicitud",
      paragraphs: [
        "Las visitas a la página de presupuesto y el hecho de haber iniciado un formulario se registran de forma seudonimizada, sin sus datos de contacto, para medir el uso del servicio.",
        "Al pulsar «Ver mi estimación», su ficha completa se crea o se actualiza con el estado «Estimación consultada — presupuesto no confirmado», el importe mostrado y la marca de tiempo. Si no solicita un presupuesto definitivo, esa ficha permanece en nuestra herramienta de seguimiento hasta que expire el plazo de conservación indicado más abajo.",
      ],
    },
    {
      title: "Destinatarios",
      bullets: [
        "Internamente: el personal de TSPVIP encargado de las reservas, la atención al cliente y la facturación, dentro de lo necesario para su función. El acceso a la herramienta de seguimiento está reservado a cuentas de administrador autenticadas.",
        "En su caso, nuestros chóferes colaboradores, únicamente con la información necesaria para realizar el servicio.",
        "Las autoridades administrativas o judiciales cuando la ley lo exija.",
      ],
      paragraphs: ["Sus datos nunca se venden ni se alquilan."],
    },
    {
      title: "Proveedores técnicos",
      paragraphs: ["Recurrimos a los siguientes proveedores, que actúan por nuestra cuenta:"],
      bullets: [
        "Alojamiento del sitio, de la base de datos y de la autenticación: Lovable Cloud (infraestructura Supabase) y Cloudflare para la distribución del sitio.",
        "Envío de correos transaccionales (confirmaciones, presupuestos): Resend.",
        "Asistente conversacional y redacción asistida: pasarela de inteligencia artificial Lovable AI.",
        "Cálculo de distancias por carretera: Google Maps Platform (API Routes).",
        "Gestión del banner y de las preferencias de cookies: Secure Privacy.",
      ],
    },
    {
      title: "Transferencias fuera de la Unión Europea",
      paragraphs: [
        "Algunos de estos proveedores están establecidos en Estados Unidos o pueden tratar datos allí. Estas transferencias se amparan en las cláusulas contractuales tipo de la Comisión Europea y, en su caso, en el Marco de Privacidad de Datos UE–EE. UU., junto con medidas técnicas como el cifrado en tránsito.",
        "Puede solicitar una copia de las garantías aplicables escribiendo a reservations@tspvip.com.",
      ],
    },
    {
      title: "Plazos de conservación",
      bullets: [
        "Estimación o presupuesto sin continuación: 12 meses desde su último contacto. Al vencimiento, la ficha se suprime o deja de ser identificativa mediante una tarea automática.",
        "Datos utilizados para prospección comercial (ofertas y novedades): 3 años desde su obtención o desde su último contacto.",
        "Reserva confirmada, factura, pago, litigio u obligación contable: se aplica por separado el plazo legal propio de cada documento, en particular diez años para los documentos contables.",
        "Conversaciones con el Conserje TSPVIP: 90 días como máximo, salvo que den lugar a una solicitud de presupuesto o a una reserva.",
        "Registros técnicos y datos de conexión: 12 meses como máximo.",
        "Prueba de una oposición comercial: se conserva de forma mínima, en una lista de exclusión separada utilizada únicamente para no volver a contactarle.",
      ],
      paragraphs: [
        "El mero paso del tiempo, la apertura de un correo electrónico o una acción interna de TSPVIP no constituyen un nuevo contacto por su parte y no prolongan estos plazos. Una estimación no confirmada no es un documento contable y no se conserva por ese motivo.",
      ],
    },
    {
      title: "Cookies y rastreadores",
      paragraphs: [
        "Las cookies estrictamente necesarias para el funcionamiento del sitio se instalan sin consentimiento. Los rastreadores de medición de audiencia o publicitarios, cuando se utilizan, solo se instalan tras su aceptación mediante el banner de cookies.",
        "Puede aceptar, rechazar o modificar su elección en cualquier momento desde ese banner y desde la configuración de su navegador. Los rastreadores de medición de audiencia no superan los 13 meses de vida y la información recogida no se conserva más de 25 meses.",
        "El rechazo de los rastreadores no esenciales no impide utilizar el formulario de presupuesto, el Conserje TSPVIP ni ver su estimación.",
      ],
    },
    {
      title: "Ofertas y novedades por correo electrónico y SMS",
      paragraphs: [
        "Las casillas para recibir nuestras ofertas por correo electrónico o SMS son facultativas y están desmarcadas por defecto. No condicionan la visualización de la estimación ni el envío del presupuesto.",
        "Registramos la fecha, la hora, el canal y la versión del texto aceptado para poder acreditar su consentimiento. Puede retirarlo en cualquier momento mediante el enlace de baja de los mensajes comerciales o escribiendo a reservations@tspvip.com: los envíos afectados cesan de inmediato y solo se conserva la información mínima necesaria para respetar su oposición.",
        "Cada mensaje comercial incluye un enlace de baja que abre una página segura de gestión de preferencias (www.tspvip.com/es/preferencias). Esa página permite desactivar por separado el correo electrónico, los SMS o ambos, sin crear cuenta, sin volver a facilitar sus datos, sin justificar su decisión y sin llamarnos por teléfono. Surte efecto de inmediato y queda registrada con fecha y hora.",
        "La retirada del consentimiento publicitario no supone la eliminación de documentos que deban conservarse por una reserva, una factura o un litigio.",
      ],
    },
    {
      title: "Sus derechos",
      paragraphs: [
        "Dispone de los derechos de acceso, rectificación, supresión, limitación del tratamiento, oposición y, cuando el tratamiento se base en el contrato o en el consentimiento, portabilidad. También puede retirar en cualquier momento un consentimiento prestado, sin que ello afecte a la licitud del tratamiento anterior.",
        "Asimismo puede dar instrucciones sobre el destino de sus datos tras su fallecimiento.",
      ],
    },
    {
      title: "Cómo ejercer sus derechos",
      paragraphs: [
        "Envíe su solicitud a reservations@tspvip.com o por correo postal a SARL TSPVIP, 31 Rue des Peupliers, 92000 Nanterre (Francia). Indique el objeto de su solicitud; en caso de duda razonable podrá pedirse un documento de identidad.",
        "Respondemos en el plazo de un mes, prorrogable dos meses más si la solicitud es compleja, en cuyo caso se le informará.",
      ],
    },
    {
      title: "Reclamación ante la autoridad de control",
      paragraphs: [
        "Si, tras habernos contactado, considera que sus derechos no se respetan, puede presentar una reclamación ante la autoridad francesa de protección de datos (CNIL), 3 place de Fontenoy, TSA 80715, 75334 París Cedex 07 (Francia).",
      ],
    },
    {
      title: "Seguridad",
      paragraphs: [
        "Aplicamos medidas técnicas y organizativas adecuadas: conexiones cifradas (HTTPS), acceso a la base de datos compartimentado mediante reglas de seguridad a nivel de fila, cuentas de administrador nominativas y autenticadas y minimización de la información almacenada en los registros técnicos.",
      ],
    },
  ],
  contactEmail: EMAIL,
  cnilLabel: "CNIL",
  cnilHref: "https://www.cnil.fr/fr/plaintes",
};

export const PRIVACY_COPY: Record<Lang, PolicyCopy> = { fr: FR, en: EN, es: ES };
